// Web port of the iOS activation step engine.
// Source of truth: client-native/src/features/activation/activationSteps.ts
// (DO NOT edit the iOS file). Step definitions, level goals, and the pure
// unlock/level helpers are reproduced EXACTLY so the PWA gates features
// (flashcards at step 1, feed at step 4) identically to iOS.

export type ActivationCheckType =
  | "totalWords"
  | "learningWords"
  | "reviewedCards"
  | "knownWords"
  | "phrases"
  | "openedFeed"
  | "completedLessons"
  | "listeningTime";

export type ActivationUnlock = "flashcards" | "feed";

export interface ActivationStepConfig {
  step: number;
  goal: number;
  label: string;
  check: ActivationCheckType;
  unlocks?: ActivationUnlock;
}

/**
 * Activation steps drive the journey state machine and the goals shown on
 * the module-complete screen. They are independent of leveling — levels are
 * computed purely from stats via `getActivationLevel` (see below). When a
 * stat-based goal in `ACTIVATION_LEVEL_GOALS` is met, the user levels up;
 * advancing/completing a step does not.
 */
export const ACTIVATION_STEPS: readonly ActivationStepConfig[] = [
  {
    step: 1,
    goal: 22,
    label: "Save 22 words",
    check: "learningWords",
    unlocks: "flashcards",
  },
  { step: 2, goal: 22, label: "Review 22 cards", check: "reviewedCards" },
  { step: 3, goal: 21, label: "Master 21 words", check: "knownWords" },
  {
    step: 4,
    goal: 8,
    label: "Complete 8 lessons",
    check: "completedLessons",
    unlocks: "feed",
  },
  { step: 5, goal: 11, label: "Highlight 11 phrases", check: "phrases" },
  { step: 6, goal: 1, label: "Open the feed", check: "openedFeed" },
  { step: 7, goal: 900, label: "15 min listening", check: "listeningTime" },
  { step: 8, goal: 1800, label: "30 min listening", check: "listeningTime" },
  { step: 9, goal: 2700, label: "45 min listening", check: "listeningTime" },
  { step: 10, goal: 3600, label: "60 min listening", check: "listeningTime" },
] as const;

export const ACTIVATION_COMPLETE_STEP = 11;
export const TOTAL_ACTIVATION_STEPS = ACTIVATION_STEPS.length;

/**
 * Threshold for the words-saved level-up (the first activation-level goal).
 *
 * Matches step 1's "save 22 words" target: completing step 1 unlocks
 * flashcards AND levels the user up to 2 in the same beat.
 */
export const WORDS_LEVEL_UP_THRESHOLD = 22;

/**
 * Threshold for the review-based level-up.
 *
 * Set to 22 to match step 1's word goal: a user who hits the activation flow
 * saves exactly 22 words before unlocking flashcards, so they can only review
 * up to 22 unique cards. Requiring 30 would be unreachable for many users
 * during the activation phase.
 */
export const REVIEW_LEVEL_UP_THRESHOLD = 22;

/**
 * Number of card reviews after which the module-complete screen swaps from
 * the "Flashcards Unlocked!" intro variant (CTA: "Review your words!") to
 * the standard step-2 progress thermometer counting toward
 * `REVIEW_LEVEL_UP_THRESHOLD`. Once the user has reviewed at least this many
 * cards we assume they understand how flashcards work, so we surface their
 * remaining-cards progress instead of re-prompting them to start.
 */
export const FLASHCARDS_UNLOCK_INTRO_REVIEWS = 3;

/**
 * Threshold for the phrase-highlight level-up.
 *
 * Matches step 5's "highlight 11 phrases" target: completing step 5 levels
 * the user up to 6. (Phrase highlighting no longer gates the feed — the feed
 * unlock moved to step 4 / "complete 8 lessons" / level 5, since most users
 * reach that milestone but few highlight phrases.)
 */
export const PHRASES_LEVEL_UP_THRESHOLD = 11;

/**
 * Stats that drive the activation-phase level computation. Mirrors the
 * level rows defined in `levelData.ts` so each row's goal can be evaluated
 * independently.
 *
 * `learningWords` drives the first activation goal while `totalWords` remains
 * available for surfaces that need the combined known + learning count.
 * `phrases` is the saved phrase count (matches the `phrases` activation step
 * check).
 */
export interface ActivationLevelStats {
  totalWords: number;
  learningWords: number;
  reviewedCards: number;
  knownWords: number;
  completedLessons: number;
  phrases: number;
  listeningTimeSeconds: number;
}

interface ActivationLevelGoal {
  stat: keyof ActivationLevelStats;
  threshold: number;
}

interface ActivationLevelOptions {
  repairMissingReviewedCards?: boolean;
}

/**
 * Activation-phase level-up goals, in the same order as the level rows
 * defined in `levelData.ts`. Meeting goal[i] (and all goals before it)
 * grants level i + 2 — i.e. completing the level-1 row's goal advances
 * you to level 2, completing the level-2 row's goal (with level-1's
 * already met) advances you to level 3, and so on.
 *
 * Goals are evaluated **sequentially**: a user who completes a later goal
 * out-of-order (e.g. finishes 8 lessons before reviewing 22 cards) does
 * NOT skip ahead. They stay at the highest level whose goal *and every
 * previous goal* are met, then earn each subsequent level only as the
 * earlier prerequisites are filled in. This keeps the activation journey
 * a one-step-at-a-time progression rather than a "max stat wins" race.
 *
 * Note: the `learningWords` and `completedLessons` goals match activation
 * steps 1 and 4 — those steps unlock flashcards and the feed respectively,
 * and also level the user up the moment they hit the unlock threshold.
 */
export const ACTIVATION_LEVEL_GOALS: readonly ActivationLevelGoal[] = [
  { stat: "learningWords", threshold: WORDS_LEVEL_UP_THRESHOLD },
  { stat: "reviewedCards", threshold: REVIEW_LEVEL_UP_THRESHOLD },
  { stat: "knownWords", threshold: 21 },
  { stat: "completedLessons", threshold: 8 },
  { stat: "phrases", threshold: PHRASES_LEVEL_UP_THRESHOLD },
  { stat: "listeningTimeSeconds", threshold: 900 },
  { stat: "listeningTimeSeconds", threshold: 1800 },
  { stat: "listeningTimeSeconds", threshold: 2700 },
  { stat: "listeningTimeSeconds", threshold: 3600 },
] as const;

/**
 * Total number of activation-phase level-ups. Used for offsetting
 * post-activation levels and the level data.
 */
export const ACTIVATION_LEVEL_COUNT = ACTIVATION_LEVEL_GOALS.length;

export function shouldRepairMissingReviewedCards(
  stats: ActivationLevelStats
): boolean {
  return (
    stats.reviewedCards < REVIEW_LEVEL_UP_THRESHOLD &&
    stats.learningWords >= WORDS_LEVEL_UP_THRESHOLD &&
    stats.knownWords >= 21 &&
    stats.completedLessons >= 8 &&
    stats.phrases >= PHRASES_LEVEL_UP_THRESHOLD &&
    stats.listeningTimeSeconds >= 3600
  );
}

/**
 * Compute the user's level during activation from their current stats.
 *
 * Level 1 is the starting level. Goals are evaluated **in order**: each
 * met goal advances the user one level, but as soon as we hit a goal
 * that hasn't been met we stop. This means a user who completes a later
 * goal out-of-order (e.g. finishes 8 lessons before reviewing 22 cards)
 * does *not* skip ahead — they stay at whatever level their consecutive
 * prefix of completed goals supports, and earn each later level only
 * once the earlier prerequisites are filled in.
 *
 * iOS parity note: on iOS this feeds maybeFireLevelUp / reconcileLevelOnLoad
 * (globalStore.ts, useActivationJourney.ts) which queue a celebratory LevelUp
 * overlay whenever the derived activation level increases. The PWA does NOT
 * auto-present those stat-driven level-up animations for activation goals
 * (levels 2-9) yet — feed/dictionary gating still works off the journey step,
 * only the celebration is absent. This is an intentional, known parity gap;
 * wire it into a web LevelUp presentation path to close it.
 */
export function getActivationLevel(
  stats: ActivationLevelStats,
  options: ActivationLevelOptions = {}
): number {
  let level = 1;
  for (let i = 0; i < ACTIVATION_LEVEL_GOALS.length; i++) {
    const goal = ACTIVATION_LEVEL_GOALS[i];
    const isMet =
      (options.repairMissingReviewedCards &&
        goal.stat === "reviewedCards" &&
        shouldRepairMissingReviewedCards(stats)) ||
      stats[goal.stat] >= goal.threshold;
    if (isMet) {
      level = i + 2;
    } else {
      break;
    }
  }
  return level;
}

export function getStepConfig(step: number): ActivationStepConfig | undefined {
  return ACTIVATION_STEPS.find((s) => s.step === step);
}

/**
 * Return the activation step that unlocks `feature` (or undefined if the
 * feature has no gate). Used by `isFeatureUnlocked` and gate copy.
 */
export function getFeatureUnlockStep(
  feature: ActivationUnlock
): ActivationStepConfig | undefined {
  return ACTIVATION_STEPS.find((s) => s.unlocks === feature);
}

/**
 * Map an `ActivationStepConfig.check` name to its corresponding key in
 * `ActivationLevelStats`. The two diverge only because step config names a
 * listening goal `listeningTime` while the level-stats key carries the unit
 * (`listeningTimeSeconds`). `openedFeed` has no level-goal counterpart.
 */
const STEP_CHECK_TO_GOAL_STAT: Record<
  ActivationCheckType,
  keyof ActivationLevelStats | null
> = {
  totalWords: "totalWords",
  learningWords: "learningWords",
  reviewedCards: "reviewedCards",
  knownWords: "knownWords",
  completedLessons: "completedLessons",
  phrases: "phrases",
  listeningTime: "listeningTimeSeconds",
  openedFeed: null,
};

/**
 * Return the activation-phase level granted when the user crosses the
 * unlock threshold for `feature`. Powers "Reach Level X to unlock" copy
 * on locked features (flashcards review, feed) so the gate text stays in
 * sync with `ACTIVATION_LEVEL_GOALS` if levels ever shift again.
 *
 * Returns `null` if the feature isn't tied to a level goal — currently
 * not the case (both `flashcards` and `feed` map cleanly), but defensive
 * for future unlock features.
 */
export function getFeatureUnlockLevel(feature: ActivationUnlock): number | null {
  const step = getFeatureUnlockStep(feature);
  if (!step) return null;
  const goalStat = STEP_CHECK_TO_GOAL_STAT[step.check];
  if (!goalStat) return null;
  const goalIndex = ACTIVATION_LEVEL_GOALS.findIndex(
    (g) => g.stat === goalStat && g.threshold === step.goal
  );
  if (goalIndex === -1) return null;
  return goalIndex + 2;
}

/**
 * Whether the user's stats meet the unlock step's OWN goal (e.g. feed →
 * completedLessons >= 8). Independent of the sequential level and the stored
 * step counter, so it can't be blocked by an unmet earlier goal or a step that
 * hasn't ticked yet.
 */
export function isFeatureGoalMet(
  feature: ActivationUnlock,
  stats: ActivationLevelStats
): boolean {
  const step = getFeatureUnlockStep(feature);
  if (!step) return false;
  const goalStat = STEP_CHECK_TO_GOAL_STAT[step.check];
  if (!goalStat) return false;
  return stats[goalStat] >= step.goal;
}

/**
 * Return the activation-phase level granted by *completing* a given step.
 * Step 1 (save 22 words) → level 2, step 2 (review 22) → level 3, etc.
 * Step 6 (open feed) is the only step without a level goal counterpart and
 * returns `null`.
 */
export function getLevelGrantedByStep(stepNumber: number): number | null {
  const stepConfig = getStepConfig(stepNumber);
  if (!stepConfig) return null;
  const goalStat = STEP_CHECK_TO_GOAL_STAT[stepConfig.check];
  if (!goalStat) return null;
  const goalIndex = ACTIVATION_LEVEL_GOALS.findIndex(
    (g) => g.stat === goalStat && g.threshold === stepConfig.goal
  );
  if (goalIndex === -1) return null;
  return goalIndex + 2;
}
